/**
 * IT Maturity Diagnostic — interactive scored quiz.
 *
 * Flow: quiz -> capture (Gravity Form) -> result.
 * The quiz and result screen are rendered here in vanilla JS; only the capture
 * step is a Gravity Form. On quiz completion we compute the score, push it into
 * the form's hidden fields, and reveal the form. When Gravity Forms confirms the
 * AJAX submission we swap the view to the computed result screen.
 *
 * Scoring is illustrative — each option carries a 0–4 score; a dimension maps to
 * a service line.
 */

const dimensions = [
    { key: 'security', label: 'Security & risk' },
    { key: 'cloud', label: 'Cloud & infrastructure' },
    { key: 'backup', label: 'Backup & recovery' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'people', label: 'People & support' },
    { key: 'strategy', label: 'Strategy & spend' },
];

const opts = (labels) => labels.map((label, i) => ({ label, score: i }));

const questions = [
    {
        dimension: 'security',
        text: 'How do you currently manage staff access and passwords?',
        options: opts([
            'Shared passwords, no real policy',
            'Individual logins, but no MFA',
            'MFA on email and key apps',
            'MFA everywhere with a password manager',
            'Conditional access and single sign-on across the board',
        ]),
    },
    {
        dimension: 'security',
        text: 'If an attacker got onto one laptop tonight, what would stop them?',
        options: opts([
            'Not sure anything would',
            'Basic antivirus only',
            'Managed endpoint protection',
            'EDR with alerting',
            'EDR feeding a 24/7 monitored SOC',
        ]),
    },
    {
        dimension: 'cloud',
        text: 'Where do your core systems and files actually live?',
        options: opts([
            'A server in the office we rarely touch',
            'Mostly on-prem, some cloud',
            'Split evenly on-prem and cloud',
            'Mostly Microsoft 365 / cloud',
            'Cloud-first with a clear architecture',
        ]),
    },
    {
        dimension: 'cloud',
        text: 'How confident are you that your cloud is set up well, not just switched on?',
        options: opts([
            'No idea how it is configured',
            'Someone set it up once, years ago',
            'It works, but has not been reviewed',
            'Reviewed occasionally',
            'Actively managed and optimised',
        ]),
    },
    {
        dimension: 'backup',
        text: 'When did you last confirm a backup could actually be restored?',
        options: opts([
            'We have never tested a restore',
            'Not that I can remember',
            'Some time in the last year',
            'Within the last quarter',
            'We run scheduled test restores',
        ]),
    },
    {
        dimension: 'backup',
        text: 'If your main system went down, how long until you were operating again?',
        options: opts([
            'We honestly do not know',
            'Days, probably',
            'About a day',
            'A few hours',
            'Under an hour, with a tested plan',
        ]),
    },
    {
        dimension: 'compliance',
        text: 'Could you pass a security audit or client due-diligence review today?',
        options: opts([
            'Definitely not',
            'We would struggle',
            'Partly, with some scrambling',
            'Mostly, a few gaps',
            'Yes, we hold a recognised certification',
        ]),
    },
    {
        dimension: 'compliance',
        text: 'How aligned are you with the Essential Eight?',
        options: opts([
            'Never heard of it',
            'Heard of it, done nothing',
            'A few controls in place',
            'Working toward Maturity Level 1–2',
            'At Maturity Level 2 or above',
        ]),
    },
    {
        dimension: 'people',
        text: 'When something breaks, how do people get help?',
        options: opts([
            'They ask whoever is free',
            'One overloaded internal person',
            'A break-fix provider we call',
            'A helpdesk with response times',
            'A managed desk with SLAs and escalation',
        ]),
    },
    {
        dimension: 'people',
        text: 'How much IT knowledge sits with a single person?',
        options: opts([
            'Everything is in one person’s head',
            'Most of it, undocumented',
            'Some documentation exists',
            'Well documented, shared',
            'Fully documented and managed externally',
        ]),
    },
    {
        dimension: 'strategy',
        text: 'How predictable is your IT spend?',
        options: opts([
            'It is a series of surprises',
            'Invoices rarely match quotes',
            'Roughly predictable',
            'Mostly fixed monthly',
            'Fixed, planned, with a roadmap',
        ]),
    },
    {
        dimension: 'strategy',
        text: 'Who owns IT strategy for the next three years?',
        options: opts([
            'Nobody, really',
            'Whoever shouts loudest',
            'An owner or manager, part-time',
            'An internal lead',
            'A vCIO with quarterly reviews',
        ]),
    },
];

const compactQuestions = [questions[0], questions[2], questions[4], questions[6], questions[8]];

function tierFor(pct) {
    if (pct < 30) return { label: 'Exposed', blurb: 'Significant gaps that need attention soon.' };
    if (pct < 55) return { label: 'Developing', blurb: 'Foundations exist, but key risks remain.' };
    if (pct < 78) return { label: 'Established', blurb: 'Solid setup with room to mature.' };
    return { label: 'Engineered', blurb: 'A mature, well-run IT function.' };
}

const dimLabel = (key) => dimensions.find((d) => d.key === key)?.label || key;

const ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
const ICON_ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';

function el(tag, className, html) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
}

function computeResult(qs, answers) {
    const perDim = {};
    qs.forEach((q, i) => {
        const s = answers[i] ?? 0;
        perDim[q.dimension] = perDim[q.dimension] || { sum: 0, count: 0 };
        perDim[q.dimension].sum += s;
        perDim[q.dimension].count += 1;
    });
    const dims = Object.entries(perDim).map(([key, v]) => ({
        key,
        label: dimLabel(key),
        pct: Math.round((v.sum / (v.count * 4)) * 100),
    }));
    const overall = dims.length
        ? Math.round(dims.reduce((a, d) => a + d.pct, 0) / dims.length)
        : 0;
    return { dims, overall, tier: tierFor(overall) };
}

function initOne(root, prefersReducedMotion) {
    const quizMount = root.querySelector('[data-diag-quiz]');
    const capture = root.querySelector('[data-diag-capture]');
    const resultMount = root.querySelector('[data-diag-result]');
    if (!quizMount || !capture || !resultMount) return;

    const variant = root.getAttribute('data-variant') === 'compact' ? 'compact' : 'full';
    const qs = variant === 'compact' ? compactQuestions : questions;
    const gfId = root.getAttribute('data-gf-id') || '';
    let fields = {};
    try {
        fields = JSON.parse(root.getAttribute('data-gf-fields') || '{}');
    } catch (e) {
        fields = {};
    }

    let current = 0;
    const answers = {};

    /* ---- Quiz ---- */
    function renderQuiz() {
        const q = qs[current];
        const answered = current + (answers[current] != null ? 1 : 0);
        const progress = Math.round((answered / qs.length) * 100);

        const card = el('div', 'card-dark p-7 sm:p-9');

        const meta = el('div', 'flex items-center justify-between text-sm');
        meta.append(
            el('span', 'font-mono font-semibold uppercase tracking-widest text-accent', dimLabel(q.dimension)),
            el('span', 'font-mono text-slate-dim', `${current + 1} / ${qs.length}`),
        );

        const track = el('div', 'mt-4 h-1.5 overflow-hidden rounded-full bg-navy-800');
        const fill = el('div', 'h-full rounded-full bg-accent transition-all duration-500 ease-spring');
        fill.style.width = `${progress}%`;
        track.append(fill);

        const heading = el('h3', 'mt-7 text-[clamp(1.25rem,2.2vw,1.6rem)] font-bold leading-snug text-white');
        heading.textContent = q.text;

        const list = el('div', 'mt-6 flex flex-col gap-3');
        q.options.forEach((o) => {
            const selected = answers[current] === o.score;
            const btn = el('button', `awdd-diag__option group flex min-h-[52px] items-center gap-3 rounded-xl border px-4 py-3 text-left text-[0.95rem] transition-all duration-200 ${
                selected
                    ? 'border-accent bg-accent-soft text-white'
                    : 'border-navy-800 bg-navy text-white/80 hover:border-accent/50 hover:text-white'
            }`);
            btn.type = 'button';
            const bullet = el('span', `flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                selected ? 'border-accent bg-accent text-white' : 'border-slate-dim/50'
            }`, selected ? `<span class="h-3 w-3">${ICON_CHECK}</span>` : '');
            btn.append(bullet, document.createTextNode(o.label));
            btn.addEventListener('click', () => choose(o.score));
            list.append(btn);
        });

        card.append(meta, track, heading, list);

        // Footer controls: Back (from q2 onward) + "See my score" on the last
        // answered question.
        const showBack = current > 0;
        const showSubmit = answers[current] != null && current === qs.length - 1;
        if (showBack || showSubmit) {
            const footer = el('div', 'mt-7 flex items-center gap-4');
            if (showBack) {
                const back = el('button', 'text-sm font-semibold text-slate-dim transition-colors hover:text-white', 'Back');
                back.type = 'button';
                back.addEventListener('click', () => {
                    current = Math.max(0, current - 1);
                    renderQuiz();
                });
                footer.append(back);
            }
            if (showSubmit) {
                const submit = el('button', 'btn-primary ml-auto !px-5 !py-2.5 text-sm', `See my score <span class="h-4 w-4">${ICON_ARROW}</span>`);
                submit.type = 'button';
                submit.addEventListener('click', showCapture);
                footer.append(submit);
            }
            card.append(footer);
        }

        quizMount.replaceChildren(card);
    }

    function choose(score) {
        answers[current] = score;
        renderQuiz(); // reflect selection immediately
        window.setTimeout(() => {
            if (current < qs.length - 1) {
                current += 1;
                renderQuiz();
            } else {
                showCapture();
            }
        }, prefersReducedMotion ? 0 : 180);
    }

    /* ---- Capture (Gravity Form) ---- */
    function showCapture() {
        const result = computeResult(qs, answers);
        // Push scores into the form's hidden fields so each entry/notification
        // carries the person's result. Gravity Forms renders hidden fields as
        // `<input name="input_{id}">`.
        const setField = (fieldId, value) => {
            if (!fieldId) return;
            const input = capture.querySelector(`input[name="input_${fieldId}"]`);
            if (input) input.value = value;
        };
        setField(fields.overall, result.overall);
        setField(fields.tier, result.tier.label);
        result.dims.forEach((d) => setField(fields[d.key], d.pct));

        quizMount.classList.add('hidden');
        capture.classList.remove('hidden');

        watchForConfirmation(result);
    }

    /* ---- Result ---- */
    function showResult(result) {
        const r = result || computeResult(qs, answers);
        const card = el('div', 'card-dark p-7 sm:p-9');

        const head = el('div', 'flex items-end gap-5 border-b border-navy-800 pb-6');
        const scoreWrap = el('div');
        const score = el('div', 'font-mono text-[clamp(3rem,7vw,4.5rem)] font-semibold leading-none text-white');
        score.innerHTML = `${r.overall}<span class="text-2xl text-slate-dim">/100</span>`;
        const tier = el('span', 'mt-2 inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-sm font-semibold text-accent');
        tier.textContent = r.tier.label;
        scoreWrap.append(score, tier);
        const blurb = el('p', 'pb-1 text-sm text-slate-dim');
        blurb.textContent = r.tier.blurb;
        head.append(scoreWrap, blurb);

        const bars = el('div', 'mt-6 space-y-4');
        r.dims.forEach((d, i) => {
            const row = el('div');
            const labelRow = el('div', 'mb-1.5 flex items-center justify-between text-sm');
            labelRow.append(
                el('span', 'text-white/85', d.label),
                el('span', 'font-mono text-slate-dim', `${d.pct}/100`),
            );
            const track = el('div', 'h-2 overflow-hidden rounded-full bg-navy-800');
            const fill = el('div', 'h-full rounded-full bg-accent transition-all duration-700 ease-spring');
            fill.style.width = prefersReducedMotion ? `${d.pct}%` : '0%';
            fill.style.transitionDelay = `${i * 90}ms`;
            track.append(fill);
            row.append(labelRow, track);
            bars.append(row);
            if (!prefersReducedMotion) {
                window.requestAnimationFrame(() => { fill.style.width = `${d.pct}%`; });
            }
        });

        const note = el('p', 'mt-6 text-xs text-slate-dim', 'Illustrative scores. Your emailed report includes tailored recommendations per dimension.');

        card.append(head, bars, note);

        capture.classList.add('hidden');
        resultMount.replaceChildren(card);
        resultMount.classList.remove('hidden');
    }

    /* Detect the Gravity Forms AJAX confirmation, then swap to the result. */
    function watchForConfirmation(result) {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            showResult(result);
        };

        // Preferred: Gravity Forms' jQuery event (fires on AJAX confirmation).
        if (window.jQuery) {
            window.jQuery(document).on('gform_confirmation_loaded', function (e, formId) {
                if (!gfId || String(formId) === String(gfId)) finish();
            });
        }

        // Fallback: watch the capture region for the confirmation markup.
        if ('MutationObserver' in window) {
            const obs = new MutationObserver(() => {
                if (capture.querySelector('.gform_confirmation_message, .gforms_confirmation_message')) {
                    obs.disconnect();
                    finish();
                }
            });
            obs.observe(capture, { childList: true, subtree: true });
        }
    }

    renderQuiz();
}

export function initDiagnostic(prefersReducedMotion = false) {
    document.querySelectorAll('[data-awdd-diagnostic]').forEach((root) => initOne(root, prefersReducedMotion));
}
